import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock, LockOpen, Pencil, Plus, Receipt, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { formatCents, OUTCOMES } from '@/components/repairs';
import { Button } from '@/components/ui/button';
import { Alert, Badge, Card, CardContent, CardHeader, CardTitle, Input, Select, Textarea } from '@/components/ui/primitives';
import type { LockState, RepairOutcome } from '@/lib/types';
import { api, errorMessage, setUnlock } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn, formatDateTime } from '@/lib/utils';

export type BillingStatus = 'READY' | 'TRANSFERRED' | 'NOT_BILLABLE';
export interface BillingLine {
  kind: 'FLAT_RATE' | 'CREDIT' | 'LABOR' | 'PART' | 'OTHER' | 'TIME';
  description: string;
  quantity: number;
  unitPriceCents: number | null;
  totalCents: number | null;
}
export interface BillingSummary {
  lines: BillingLine[];
  taxRate: number;
  net: number;
  tax: number;
  gross: number;
  hints: string[];
}
export interface BillingRecord {
  id: string;
  status: BillingStatus;
  type: 'ticket' | 'repair';
  ref: { id: string; number: string; title: string; state: string };
  customer: { id: string; name: string; number: string } | null;
  note: string | null;
  releasedAt: string;
  releasedByName: string | null;
  processedAt: string | null;
  processedByName: string | null;
  reference: string | null;
  processNote: string | null;
  changeRequest: { requestedAt: string; requestedByName: string | null; reason: string } | null;
  net: number | null;
  minutes: number | null;
}
export interface BillingDetail extends BillingRecord {
  summary: BillingSummary;
}
export interface FlatRate {
  id: string;
  name: string;
  description: string | null;
  priceCents: number;
  creditOnRepair: boolean;
  autoAdd: boolean;
  active: boolean;
  sortOrder: number;
  usage: number;
}
interface Charge {
  id: string;
  name: string;
  priceCents: number;
  creditOnRepair: boolean;
  createdAt: string;
}

export const BILLING_STATUS: Record<BillingStatus, { label: string; variant: 'warning' | 'success' | 'outline' }> = {
  READY: { label: 'Bereit zur Rechnung', variant: 'warning' },
  TRANSFERRED: { label: 'In Powerbird übernommen', variant: 'success' },
  NOT_BILLABLE: { label: 'Keine Rechnung', variant: 'outline' },
};

export const BillingBadge = ({ status }: { status: BillingStatus }) => <Badge variant={BILLING_STATUS[status].variant}>{BILLING_STATUS[status].label}</Badge>;

const LINE_KIND: Record<BillingLine['kind'], string> = { FLAT_RATE: 'Pauschale', CREDIT: 'Verrechnung', LABOR: 'Arbeitszeit', PART: 'Material', OTHER: 'Sonstiges', TIME: 'Zeit' };
const onError = (e: unknown) => toast.error(errorMessage(e));

/** Positionen und Summen (netto); Zeiten ohne Preis werden in Minuten angezeigt. */
export function BillingLines({ summary, compact = false }: { summary: BillingSummary; compact?: boolean }) {
  const priced = summary.lines.some((l) => l.totalCents !== null);
  return (
    <div className="grid gap-2 text-sm">
      {summary.lines.length === 0 ? (
        <p className="text-muted-foreground">Keine Positionen erfasst.</p>
      ) : (
        <table className="w-full text-left">
          <tbody>
            {summary.lines.map((l, i) => (
              <tr key={i} className="border-b last:border-0">
                <td className="py-1 pr-2 align-top">
                  {l.description}
                  {!compact && <span className="block text-xs text-muted-foreground">{LINE_KIND[l.kind]}</span>}
                </td>
                <td className="whitespace-nowrap py-1 text-right align-top tabular-nums">
                  {l.totalCents !== null ? (
                    <span className={l.totalCents < 0 ? 'text-success' : undefined}>
                      {l.kind === 'TIME' ? `${l.quantity} Min. × ${formatCents(l.unitPriceCents ?? 0)}/h = ` : l.quantity > 1 ? `${l.quantity} × ${formatCents(l.unitPriceCents ?? 0)} = ` : ''}
                      {formatCents(l.totalCents)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">{l.quantity} Min.</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {priced && (
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 text-right tabular-nums">
          <dt className="text-muted-foreground">Netto</dt>
          <dd>{formatCents(summary.net)}</dd>
          <dt className="text-muted-foreground">USt. {summary.taxRate} %</dt>
          <dd>{formatCents(summary.tax)}</dd>
          <dt className="font-medium">Gesamt</dt>
          <dd className="font-medium">{formatCents(summary.gross)}</dd>
        </dl>
      )}
      {summary.hints.map((h) => (
        <p key={h} className="text-xs text-muted-foreground">
          {h}
        </p>
      ))}
    </div>
  );
}

/** Abrechnungsstand in der Ticket- bzw. Reparaturakte mit Freigabe „Bereit zur Rechnung“. */
export function BillingCard({ ticketId, repairId, repairStatus, repairOutcome }: { ticketId?: string; repairId?: string; repairStatus?: string; repairOutcome?: RepairOutcome | null }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [note, setNote] = useState('');
  // Reparatur noch nicht fertig: Freigabe = Fertigmeldung, abholbereit nach der Rechnung
  const needsOutcome = Boolean(repairId && repairStatus && !['READY_FOR_PICKUP', 'PICKED_UP', 'CANCELLED'].includes(repairStatus));
  const [outcome, setOutcome] = useState<RepairOutcome>(repairOutcome ?? 'REPAIRED');
  const [open, setOpen] = useState(false);
  const allowed = can('billing.read') || can('billing.release');
  const ref = ticketId ? { ticketId } : { repairId };
  const query = ticketId ? `ticketId=${ticketId}` : `repairId=${repairId}`;
  const key = ['billing', 'ref', ticketId ?? repairId];
  const { data } = useQuery({ queryKey: key, queryFn: () => api<{ record: BillingDetail | null; preview: BillingSummary }>(`/billing/ref?${query}`), enabled: allowed });
  const done = () => {
    qc.invalidateQueries({ queryKey: ['billing'] });
    qc.invalidateQueries({ queryKey: repairId ? ['repairs'] : ['tickets'] });
  };
  const release = useMutation({
    mutationFn: () => api('/billing/release', { method: 'POST', body: { ...ref, note: note || null, ...(needsOutcome ? { outcome } : {}) } }),
    onSuccess: () => {
      toast.success('Bereit zur Rechnung – die Buchhaltung sieht den Vorgang jetzt');
      setOpen(false);
      setNote('');
      done();
    },
    onError,
  });
  const withdraw = useMutation({ mutationFn: () => api('/billing/withdraw', { method: 'POST', body: ref }), onSuccess: done, onError });
  if (!allowed || !data) return null;
  const r = data.record;
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-sm">
          <Receipt className="size-4" /> Abrechnung
        </CardTitle>
        {r ? <BillingBadge status={r.status} /> : <Badge variant="outline">nicht freigegeben</Badge>}
      </CardHeader>
      <CardContent className="grid gap-3">
        <BillingLines summary={r?.summary ?? data.preview} compact />
        {r && (
          <div className="grid gap-1 border-t pt-2 text-xs text-muted-foreground">
            <span>
              Freigegeben {formatDateTime(r.releasedAt)}
              {r.releasedByName ? ` von ${r.releasedByName}` : ''}
            </span>
            {r.note && <span className="text-foreground">Hinweis: {r.note}</span>}
            {r.processedAt && (
              <span>
                {BILLING_STATUS[r.status].label} {formatDateTime(r.processedAt)}
                {r.processedByName ? ` von ${r.processedByName}` : ''}
                {r.reference ? ` · Beleg ${r.reference}` : ''}
              </span>
            )}
            {r.processNote && <span className="text-foreground">Buchhaltung: {r.processNote}</span>}
          </div>
        )}
        {can('billing.release') && !r && !open && (
          <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
            <Receipt /> Bereit zur Rechnung
          </Button>
        )}
        {open && (
          <div className="grid gap-2">
            {needsOutcome && (
              <label className="grid gap-1 text-sm">
                Ergebnis der Reparatur
                <Select value={outcome} onChange={(e) => setOutcome(e.target.value as RepairOutcome)} aria-label="Ergebnis der Reparatur">
                  {(Object.keys(OUTCOMES) as RepairOutcome[]).map((o) => (
                    <option key={o} value={o}>
                      {OUTCOMES[o]}
                    </option>
                  ))}
                </Select>
                <span className="text-xs text-muted-foreground">Das Gerät wird automatisch abholbereit (mit E-Mail an den Kunden), sobald die Buchhaltung die Rechnung erstellt hat.</span>
              </label>
            )}
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Hinweis an die Buchhaltung (optional), z. B. Anfahrt berechnen" aria-label="Hinweis an die Buchhaltung" />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => release.mutate()} loading={release.isPending}>
                Freigeben
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}
        {(can('billing.manage') || can('admin.records.unlock')) && r?.status === 'READY' && (
          <Button variant="ghost" size="sm" onClick={() => withdraw.mutate()} loading={withdraw.isPending}>
            <Undo2 /> Freigabe zurücknehmen
          </Button>
        )}
        {r?.changeRequest && (
          <p className="rounded-md border border-warning/40 bg-warning/10 p-2 text-xs">
            Änderungsantrag von {r.changeRequest.requestedByName ?? 'unbekannt'} ({formatDateTime(r.changeRequest.requestedAt)}): „{r.changeRequest.reason}“ – wartet auf die Buchhaltung.
          </p>
        )}
        {r && !r.changeRequest && can('billing.release') && !can('billing.manage') && !can('admin.records.unlock') && <ChangeRequestButton target={ref} onDone={done} />}
        {can('billing.read') && r && (
          <Link to={`/billing?id=${r.id}`} className="text-xs text-primary hover:underline">
            In der Abrechnung öffnen
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

/** Änderungsantrag für einen zur Abrechnung gesperrten Vorgang. */
function ChangeRequestButton({ target, onDone }: { target: { ticketId?: string; repairId?: string }; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const send = useMutation({
    mutationFn: () => api('/billing/change-request', { method: 'POST', body: { ...target, reason } }),
    onSuccess: () => {
      toast.success('Änderungsantrag an die Buchhaltung gesendet');
      setOpen(false);
      setReason('');
      onDone();
    },
    onError,
  });
  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Pencil /> Änderung beantragen
      </Button>
    );
  }
  return (
    <div className="grid gap-2">
      <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Was soll geändert werden und warum?" aria-label="Begründung" autoFocus />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => send.mutate()} loading={send.isPending} disabled={reason.trim().length < 5}>
          Antrag senden
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
          Abbrechen
        </Button>
      </div>
    </div>
  );
}

/**
 * Bearbeitung eines gesperrten Vorgangs bewusst aktivieren (nur mit Recht admin.records.unlock).
 * Gilt für die Pfade unter `prefix` (z. B. /repairs/<id>), bis sie deaktiviert wird oder die Seite verlassen wird.
 */
export function useUnlock(prefix: string, lock: LockState | undefined) {
  const [reason, setReason] = useState<string | null>(null);
  useEffect(() => () => setUnlock(prefix, null), [prefix]);
  // Ohne Sperre (z. B. nach Genehmigung) entfällt die Entsperrung
  useEffect(() => {
    if (lock && !lock.kind && reason) {
      setUnlock(prefix, null);
      setReason(null);
    }
  }, [lock, prefix, reason]);
  return {
    unlocked: Boolean(reason),
    reason,
    activate: (r: string) => {
      setUnlock(prefix, r);
      setReason(r);
    },
    deactivate: () => {
      setUnlock(prefix, null);
      setReason(null);
    },
  };
}

/** Hinweis oben im Vorgang, wenn er gesperrt ist – mit „Bearbeitung aktivieren“ für Administratoren. */
export function LockBanner({ lock, unlock, entity = 'repair' }: { lock: LockState; unlock: ReturnType<typeof useUnlock>; entity?: 'repair' | 'ticket' }) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState('');
  if (!lock.kind) return null;
  const text =
    lock.kind === 'closed'
      ? 'Die Reparatur ist abgeschlossen und schreibgeschützt.'
      : entity === 'ticket'
        ? 'Zur Abrechnung freigegeben – Zeiten, Kunde und Ansprechpartner nur über einen Änderungsantrag (Karte „Abrechnung“).'
        : 'Zur Abrechnung freigegeben – Änderungen nur über einen Änderungsantrag (Karte „Abrechnung“).';
  return (
    <Alert className={cn('mb-4 grid gap-2', unlock.unlocked && 'border-warning bg-warning/15')}>
      <span className="flex items-start gap-2">
        {unlock.unlocked ? <LockOpen className="mt-0.5 size-4 shrink-0" /> : <Lock className="mt-0.5 size-4 shrink-0" />}
        <span>
          {unlock.unlocked ? (
            <>
              <strong>Bearbeitung aktiviert</strong> – Grund: „{unlock.reason}“. Jede Änderung wird im Audit-Log protokolliert.
            </>
          ) : (
            text
          )}
        </span>
      </span>
      {lock.canOverride &&
        (unlock.unlocked ? (
          <span>
            <Button size="sm" variant="outline" onClick={unlock.deactivate}>
              <Lock /> Wieder sperren
            </Button>
          </span>
        ) : asking ? (
          <span className="flex flex-wrap items-center gap-2">
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Begründung (wird protokolliert)" aria-label="Begründung" className="h-8 max-w-md" autoFocus />
            <Button
              size="sm"
              disabled={reason.trim().length < 3}
              onClick={() => {
                unlock.activate(reason.trim());
                setAsking(false);
                setReason('');
              }}
            >
              Aktivieren
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAsking(false)}>
              Abbrechen
            </Button>
          </span>
        ) : (
          <span>
            <Button size="sm" variant="outline" onClick={() => setAsking(true)}>
              <LockOpen /> Bearbeitung aktivieren
            </Button>
          </span>
        ))}
    </Alert>
  );
}

/** Pauschalen einer Reparatur (z. B. Diagnose/KVA) mit Hinweis auf die Verrechnung. */
export function RepairChargesCard({ repairId, locked }: { repairId: string; locked: boolean }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const canEdit = !locked && (can('repairs.work') || can('repairs.intake') || can('repairs.quotes'));
  const [pick, setPick] = useState('');
  const { data: charges } = useQuery({ queryKey: ['repairs', 'charges', repairId], queryFn: () => api<Charge[]>(`/repairs/${repairId}/charges`) });
  const { data: rates } = useQuery({ queryKey: ['repair-flat-rates'], queryFn: () => api<{ items: FlatRate[] }>('/repair-flat-rates'), enabled: canEdit });
  const changed = (list: Charge[]) => {
    qc.setQueryData(['repairs', 'charges', repairId], list);
    qc.invalidateQueries({ queryKey: ['billing', 'ref', repairId] });
    qc.invalidateQueries({ queryKey: ['repairs', 'detail', repairId] });
  };
  const add = useMutation({
    mutationFn: (flatRateId: string) => api<Charge[]>(`/repairs/${repairId}/charges`, { method: 'POST', body: { flatRateId } }),
    onSuccess: (l) => (changed(l), setPick('')),
    onError,
  });
  const remove = useMutation({ mutationFn: (id: string) => api<Charge[]>(`/repairs/${repairId}/charges/${id}`, { method: 'DELETE' }), onSuccess: changed, onError });
  const available = rates?.items.filter((r) => r.active) ?? [];
  if (!charges || (charges.length === 0 && !canEdit)) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Pauschalen</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2 text-sm">
        {charges.length === 0 && <p className="text-muted-foreground">Keine Pauschalen.</p>}
        {charges.map((c) => (
          <div key={c.id} className="flex items-start justify-between gap-2">
            <span>
              {c.name} <span className="tabular-nums text-muted-foreground">{formatCents(c.priceCents)}</span>
              {c.creditOnRepair && <span className="block text-xs text-muted-foreground">wird bei Durchführung der Reparatur verrechnet</span>}
            </span>
            {canEdit && (
              <Button variant="ghost" size="icon" className="size-7" aria-label={`${c.name} entfernen`} onClick={() => remove.mutate(c.id)}>
                <Trash2 className="size-3.5" />
              </Button>
            )}
          </div>
        ))}
        {canEdit && available.length > 0 && (
          <div className="flex gap-2 border-t pt-2">
            <Select value={pick} onChange={(e) => setPick(e.target.value)} aria-label="Pauschale wählen" className="h-8 text-xs">
              <option value="">Pauschale hinzufügen …</option>
              {available.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} ({formatCents(r.priceCents)})
                </option>
              ))}
            </Select>
            <Button size="icon" variant="outline" className="size-8 shrink-0" disabled={!pick} onClick={() => add.mutate(pick)} aria-label="Hinzufügen">
              <Plus />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
